<template>
  <div>
    <AssignmentStripe />
    <div class="container">
      <div class="assignment">
        <div class="assignment__body table-shadow">
          <div class="assignment__title d-flex justify-content-between align-items-center">
            <span>Details</span>
            <div>
              <b-dropdown class="wheel-no-outline" no-caret>
                <template #button-content>
                  <mdicon name="CogOutline" class="right" />
                </template>
                <DropdownItem
                  v-for="actionItem of actions"
                  :key="actionItem.handle"
                  :action="actionItem"
                  :assignment="assignment"
                />
              </b-dropdown>
            </div>
          </div>

          <div class="row mb-3">
            <div class="col-3">
              <div class="assignment__subtitle mb-1">Distribution channel</div>
              <div class="assignment__subtitle assignment__subtitle_color-grey mb-1">
                {{ distributionChannel }}
              </div>
            </div>
            <div class="col-3">
              <div class="assignment__subtitle mb-1">Service level</div>
              <div class="assignment__subtitle assignment__subtitle_color-grey mb-1">
                <div>
                  {{ serviceLevel }}
                </div>
                <div v-if="serviceLevelComment">
                  <small>{{ serviceLevelComment }}</small>
                </div>
              </div>
            </div>
            <div class="col-3">
              <div class="assignment__subtitle mb-1">Assignment reference</div>
              <div class="assignment__subtitle assignment__subtitle_color-grey mb-1">
                {{ assignment.reference }}
              </div>
            </div>
            <div class="col-3">
              <div class="assignment__subtitle mb-1">Item (shipment) reference</div>
              <div class="assignment__subtitle assignment__subtitle_color-grey mb-1">
                {{ assignment.item?.orderReference }}
              </div>
            </div>
          </div>

          <hr class="delimiter" />

          <div v-if="assignment.pickupDate">
            <div class="assignment__title">Pickup</div>

            <div class="row mb-3">
              <div class="col-3">
                <div class="assignment__subtitle mb-1">Date</div>
                <div class="assignment__subtitle assignment__subtitle_color-grey mb-1">
                  {{ formattedDate(assignment.pickupDate, 'MMMM DD') }}
                </div>
              </div>
              <div class="col-3">
                <div class="assignment__subtitle mb-1">Instructions</div>
                <div class="assignment__subtitle assignment__subtitle_color-grey mb-1">
                  {{ assignment.pickupInstructions }}
                </div>
              </div>
            </div>

            <hr class="delimiter" />
          </div>

          <div v-if="assignment.senderAddress">
            <div class="assignment__title">{{ isReturn ? 'Recipient (return to)' : 'Sender' }}</div>
            <div class="row mb-3">
              <div class="col-3">
                <div class="assignment__subtitle mb-1">Name</div>
                <div class="assignment__subtitle assignment__subtitle_color-grey mb-1">
                  {{ sender }}
                </div>
              </div>
              <div class="col-9">
                <div class="assignment__subtitle mb-1">Address</div>
                <div class="assignment__subtitle assignment__subtitle_color-grey mb-1">
                  {{ toAddressString(assignment.senderAddress) }}
                </div>
              </div>
            </div>

            <hr class="delimiter" />
          </div>

          <div v-if="assignment.recipient">
            <div class="assignment__title">{{ isReturn ? 'Sender (customer)' : 'Recipient' }}</div>
            <div class="row mb-3">
              <div class="col-3">
                <div class="assignment__subtitle mb-1">Name</div>
                <div class="assignment__subtitle assignment__subtitle_color-grey mb-1">
                  {{ addressOwner(assignment.recipient) }}
                </div>
                <div
                  v-if="
                    (assignment.item?.validationFlags?.firstName?.reason &&
                      assignment.item?.validationFlags?.firstName?.level === 'fatal') ||
                      (assignment.item?.validationFlags?.lastName?.reason &&
                        assignment.item?.validationFlags?.lastName?.level === 'fatal')
                  "
                  class="text-danger-reverse mt-1"
                >
                  {{ assignment.item?.validationFlags?.firstName?.reason || '' }}
                  {{ assignment.item?.validationFlags?.lastName?.reason || '' }}
                </div>
              </div>
              <div class="col-4">
                <div class="assignment__subtitle mb-1">Address</div>
                <div class="assignment__subtitle assignment__subtitle_color-grey mb-1">
                  {{ toAddressString(assignment.recipient) }}
                </div>
                <div
                  v-if="
                    (assignment.item?.validationFlags?.postalCode?.reason &&
                      assignment.item?.validationFlags?.postalCode?.level === 'fatal') ||
                      (assignment.item?.validationFlags?.addressLine?.reason &&
                        assignment.item?.validationFlags?.addressLine?.level === 'fatal')
                  "
                  class="text-danger-reverse mt-1"
                >
                  {{ assignment.item?.validationFlags?.postalCode?.reason || '' }}
                  {{ assignment.item?.validationFlags?.addressLine?.reason || '' }}
                </div>
              </div>
              <div class="col-2">
                <div class="assignment__subtitle mb-1">Email</div>
                <div class="assignment__subtitle assignment__subtitle_color-grey mb-1">
                  {{ assignment.recipient.email }}
                </div>
                <div
                  v-if="
                    assignment.item?.validationFlags?.email?.reason &&
                      assignment.item?.validationFlags?.email?.level === 'fatal'
                  "
                  class="text-danger-reverse mt-1"
                >
                  {{ assignment.item?.validationFlags?.email?.reason || '' }}
                </div>
              </div>
              <div class="col-2">
                <div class="assignment__subtitle mb-1">Phone</div>
                <div class="assignment__subtitle assignment__subtitle_color-grey mb-1">
                  {{ assignment.recipient.phoneNumber }}
                </div>
                <div
                  v-if="
                    assignment.item?.validationFlags?.phoneNumber?.reason &&
                      assignment.item?.validationFlags?.phoneNumber?.level === 'fatal'
                  "
                  class="text-danger-reverse mt-1"
                >
                  {{ assignment.item?.validationFlags?.phoneNumber?.reason || '' }}
                </div>
              </div>
            </div>

            <hr class="delimiter" />
          </div>

          <div v-if="isInternational && !hasCustomInvoice">
            <div class="assignment__title">Customs Invoice</div>

            <div class="d-flex">
              <div class="assignment__subtitle mb-1">Category:</div>
              <div class="assignment__subtitle assignment__subtitle_color-grey mb-1 ml-2">
                {{ invoiceCategory }}
              </div>
            </div>

            <div v-if="products && products.length > 0" class="row mb-3">
              <div class="col-12">
                <table class="table-report bg-grey-title">
                  <thead>
                    <tr>
                      <th>Quantity</th>
                      <th>Country</th>
                      <th>Content (in English)</th>
                      <th>Commodity code</th>
                      <th>Unit weight (g)</th>
                      <th>Unit value</th>
                      <th v-text="`Total value (${currency})`" />
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="(product, index) in products" :key="'product' + index">
                      <td>{{ product.quantity }}</td>
                      <td>{{ product.countryCode }}</td>
                      <td>{{ (product.description || '').trim() ? product.description : '' }}</td>
                      <td>{{ product.commodityCode }}</td>
                      <td>{{ product.weight || product.unitWeight }}</td>
                      <td>{{ product.estimatedCost || product.unitCost }}</td>
                      <td>
                        {{ product.quantity * Number(product.estimatedCost || product.unitCost || 0) }}
                      </td>
                    </tr>
                    <tr>
                      <td colspan="6" class="border-0" />
                      <td>
                        <div class="total-count">
                          {{ totalInvoiceCosts || 0 }}
                        </div>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <hr class="delimiter" />
          </div>

          <div>
            <div class="assignment__title">Package details</div>
            <div class="d-flex mb-5">
              <div class="flex-fill">
                <div class="assignment__subtitle mb-1">Weight</div>
                <div class="assignment__subtitle assignment__subtitle_color-grey mb-1">
                  {{ assignment.item?.weight }} g
                </div>

                <div
                  v-if="
                    assignment.item?.validationFlags?.weight?.reason &&
                      assignment.item?.validationFlags?.weight?.level === 'fatal'
                  "
                  class="text-danger-reverse mt-1"
                >
                  {{ assignment.item?.validationFlags?.weight?.reason || '' }}
                </div>
              </div>
              <div class="flex-fill">
                <div class="assignment__subtitle mb-1">Height</div>
                <div class="assignment__subtitle assignment__subtitle_color-grey mb-1">
                  {{ assignment.item?.height }} cm
                </div>
                <div
                  v-if="
                    assignment.item?.validationFlags?.height?.reason &&
                      assignment.item?.validationFlags?.height?.level === 'fatal'
                  "
                  class="text-danger-reverse mt-1"
                >
                  {{ assignment.item?.validationFlags?.height?.reason || '' }}
                </div>
              </div>
              <div class="flex-fill">
                <div class="assignment__subtitle mb-1">Length</div>
                <div class="assignment__subtitle assignment__subtitle_color-grey mb-1">
                  {{ assignment.item?.length }} cm
                </div>
                <div
                  v-if="
                    assignment.item?.validationFlags?.length?.reason &&
                      assignment.item?.validationFlags?.length?.level === 'fatal'
                  "
                  class="text-danger-reverse mt-1"
                >
                  {{ assignment.item?.validationFlags?.length?.reason || '' }}
                </div>
              </div>
              <div class="flex-fill">
                <div class="assignment__subtitle mb-1">Width</div>
                <div class="assignment__subtitle assignment__subtitle_color-grey mb-1">
                  {{ assignment.item?.width }} cm
                </div>
                <div
                  v-if="
                    assignment.item?.validationFlags?.width?.reason &&
                      assignment.item?.validationFlags?.width?.level === 'fatal'
                  "
                  class="text-danger-reverse mt-1"
                >
                  {{ assignment.item?.validationFlags?.width?.reason || '' }}
                </div>
              </div>
              <div v-if="isInternational" class="flex-fill">
                <div class="assignment__subtitle mb-1">Worth</div>
                <div class="assignment__subtitle assignment__subtitle_color-grey mb-1">
                  {{ formattedPrice(assignment.item?.estimatedCost, currency) }}
                </div>
                <div
                  v-if="
                    assignment.item?.validationFlags?.estimatedCost?.reason &&
                      assignment.item?.validationFlags?.estimatedCost?.level === 'fatal'
                  "
                  class="text-danger-reverse mt-1"
                >
                  {{ assignment.item?.validationFlags?.estimatedCost?.reason || '' }}
                </div>
              </div>
              <div v-if="outsideEu" class="flex-fill">
                <div class="assignment__subtitle mb-1">Commodity code</div>
                <div class="assignment__subtitle assignment__subtitle_color-grey mb-1">
                  {{ assignment.item?.products?.[0]?.commodityCode }}
                </div>
                <div
                  v-if="
                    assignment.item?.validationFlags?.commodity_code?.reason &&
                      assignment.item?.validationFlags?.commodity_code?.level === 'fatal'
                  "
                  class="text-danger-reverse mt-1"
                >
                  {{ assignment.item?.validationFlags?.commodity_code?.reason || '' }}
                </div>
              </div>
              <div class="flex-fill">
                <div class="assignment__subtitle mb-1">Description / Ref</div>
                <div class="assignment__subtitle assignment__subtitle_color-grey mb-1">
                  {{ (assignment.item?.products?.[0]?.description || '').trim() ? assignment.item?.products?.[0]?.description : '' }}
                </div>
                <div
                  v-if="
                    assignment.item?.validationFlags?.description?.reason &&
                      assignment.item?.validationFlags?.description?.level === 'fatal'
                  "
                  class="text-danger-reverse mt-1"
                >
                  {{ assignment.item?.validationFlags?.description || '' }}
                </div>
              </div>
            </div>
          </div>

          <hr class="delimiter" />

          <div>
            <div class="assignment__title">Optimisation details</div>
            <div v-if="assignment.item?.carrierHandle" class="row mb-5">
              <div class="col-3">
                <div class="assignment__subtitle mb-1">Carrier</div>
                <div class="assignment__subtitle assignment__subtitle_color-grey mb-1">
                  <CarrierRowWithIcon :carrier="assignment.item.carrierHandle" :only-icon="false" />
                </div>
              </div>
              <div v-if="!isHidePrice" class="col-3">
                <div class="assignment__subtitle mb-1">Shipping cost</div>
                <div class="assignment__subtitle assignment__subtitle_color-grey mb-1">
                  <CostCell :assignment="assignment" />
                </div>
              </div>
              <div v-if="assignment.item.shipmentNumber" class="col-3">
                <div class="assignment__subtitle mb-1">Shipping number</div>
                <div class="assignment__subtitle assignment__subtitle_color-grey mb-1">
                  <span class="tooltip-text tracking">
                    <a :href="getLinkToTrackingPage(assignment.item.shipmentNumber, assignment.company?.branding)" target="_blank">
                      {{ assignment.item.shipmentNumber }}
                    </a>
                  </span>
                </div>
              </div>
            </div>
          </div>

          <hr class="delimiter" />

          <div class="row mb-5 summary-table">
            <ShipmentOptimisationSummary v-if="isStaff" :shipment="assignment.item" />
          </div>

          <FixErrors
            v-if="assignment.domesticErrorsCount > 0 || assignment.internationalErrorsCount > 0"
            button-class="mb-3 w-100"
          />
          <Open v-if="assignment.status === OPEN" />
          <ReportReleased v-if="assignment.status === REPORT_RELEASED" />
          <LabelsReleased v-else-if="assignment.status === LABELS_RELEASED" />
        </div>
      </div>
    </div>
  </div>
  <FixErrorsModal />
</template>

<script lang="ts">
import type { Action } from '@/types/Components/Actions'
import type { SelectOptions } from '@/types/Components/SelectOptions'
import type { Assignment } from '@/types/Models/Assignment'
import type { Product } from '@/types/Models/Product'
import { BDropdown } from 'bootstrap-vue-next'
import { ref, computed, defineComponent, watch, type Ref, type ComputedRef } from 'vue'
import { useAddressTransformers } from '@/composables/Address/useAddressTransformers'
import {
  AssignmentSettingsConfig,
  filterAction,
  prepareAction,
  sortAction,
  transformAction,
} from '@/composables/Assignment/useActionHandlers'
import { ECONOMY } from '@/const/AssignmentServiceLevels'
import { COMPLETE, LABELS_RELEASED, OPEN, REPORT_ACCEPTED, REPORT_RELEASED } from '@/const/AssignmentStatuses'
import { RETURN } from '@/const/AssignmentTypes'
import { LETTER } from '@/const/DistributionChannels'
import { OTHER } from '@/const/ProformaInvoiceTypes'
import { formattedDate, formattedPrice } from '@/services/Helpers'
import { getLinkToTrackingPage } from '@/services/Tracking'
import {
  getDeliveryTypeLabel,
  getDistributionChannelTxt,
  getProformaInvoiceTypes,
  getServiceLevelTxt,
} from '@/services/Utils'
import { useAccountStore } from '@/store/account'
import { useAssignmentSummaryStore } from '@/store/assignmentSummary'
import Open from '@/views/Components/Assignment/Actions/Buttons/Open.vue'
import CostCell from '@/views/Components/Assignment/Cells/Cost.vue'
import FixErrors from '@/views/Components/Assignment/Actions/Buttons/FixErrors.vue'
import countriesEU from '@/assets/files/countriesEU.json'
import DropdownItem from '@/views/Components/Assignment/Cells/DropdownItem.vue'
import FixErrorsModal from '@/views/Modals/Assignment/FixErrorsModal.vue'
import LabelsReleased from '@/views/Components/Assignment/Actions/Buttons/LabelsReleased.vue'
import ReportReleased from '@/views/Components/Assignment/Actions/Buttons/ReportReleased.vue'
import AssignmentStripe from '@/views/Components/Assignment/Stripe/AssignmentStripe.vue'
import CarrierRowWithIcon from '@/views/Components/Assignment/CarrierRowWithIcon.vue'
import ShipmentOptimisationSummary from '@/views/Components/Shipments/Details/OptimisationSummary.vue'

export default defineComponent({
  name: 'SingleAssignmentDetails',
  components: {
    ShipmentOptimisationSummary,
    DropdownItem,
    BDropdown,
    FixErrorsModal,
    FixErrors,
    CarrierRowWithIcon,
    AssignmentStripe,
    ReportReleased,
    LabelsReleased,
    CostCell,
    Open,
  },
  setup() {
    const assignmentSummaryStore = useAssignmentSummaryStore()
    const accountStore = useAccountStore()

    const assignment: ComputedRef<Assignment> = computed(() => assignmentSummaryStore.assignment as Assignment)
    const isStaff: ComputedRef<boolean> = computed(() => accountStore.isStaff)
    const isHidePrice: ComputedRef<boolean> = computed(() => !isStaff.value && assignment.value?.company?.hidePrice)
    const isReturn: ComputedRef<boolean> = computed(() => assignment.value?.type === RETURN)

    const documentsUrl: Ref<string | null> = ref(null)
    const showDownloadButton: Ref<boolean> = ref(true)
    const hasCustomInvoice: Ref<boolean> = ref(false)
    const isLabelCreated: Ref<boolean> = ref(false)
    const totalInvoiceCosts: Ref<number> = ref(0)
    const currency: Ref<string> = ref('SEK')
    const products: Ref<Product[]> = ref([])

    const serviceLevelComment = computed(() =>
      assignment.value.distributionChannel === LETTER
        ? 'Tracking not available.'
        : assignment.value.serviceLevel === ECONOMY
          ? '1-4 days, cheapest. Tracking available.'
          : '',
    )

    const categories: ComputedRef<Record<string, SelectOptions>> = computed(
      getProformaInvoiceTypes as unknown as () => Record<string, SelectOptions>,
    )
    const invoiceCategory: ComputedRef<string | null> = computed(
      () => categories.value[assignment.value.item?.invoiceType || OTHER]?.text || null,
    )

    const distributionChannel: ComputedRef<string> = computed(() =>
      getDistributionChannelTxt(assignment.value.distributionChannel),
    )
    const serviceLevel = computed(() => getServiceLevelTxt(assignment.value.serviceLevel || null))

    const { toAddressString, addressOwner, addressCompany } = useAddressTransformers()

    const isInternational: ComputedRef<boolean> = computed(
      () => !!assignment.value.recipient?.countryCode && assignment.value.recipient.countryCode !== 'SE',
    )
    const outsideEu: ComputedRef<boolean> = computed(
      () =>
        !!assignment.value.recipient?.countryCode &&
        !countriesEU.includes(assignment.value.recipient.countryCode),
    )

    const sender: ComputedRef<string> = computed(() =>
      assignment.value.senderAddress?.companyName
        ? assignment.value.senderAddress.companyName
        : addressCompany(assignment.value.company),
    )

    watch(
      assignment,
      () => {
        showDownloadButton.value = assignment.value.status !== COMPLETE
        isLabelCreated.value = assignment.value.item?.labelsCreationStatus !== 'pending'
        documentsUrl.value = assignment.value.documentsUrl
        currency.value = assignment.value.item?.currency ?? 'EUR'

        // For international shipments, use cn22.products if available, otherwise fall back to item.products
        const cn22Products = assignment.value.item?.cn22?.products
        if (cn22Products && cn22Products.length > 0) {
          products.value = cn22Products.map((p) => ({
            quantity: p.quantity || 1,
            countryCode: p.countryCode || '',
            description: p.description || '',
            commodityCode: p.commodityCode || '',
            weight: String(p.weight || 0),
            estimatedCost: String(p.estimatedCost || 0),
          })) as Product[]
        } else {
          products.value = assignment.value.item?.products ?? []
        }

        hasCustomInvoice.value = products.value.length === 0
        totalInvoiceCosts.value = products.value.reduce(
          (acc: number, cv: Product) =>
            acc + Number(cv.quantity || 0) * Number(cv.estimatedCost || cv.unitCost || 0),
          0,
        )
      },
      { immediate: true },
    )

    const actions: ComputedRef<Action[]> = computed(() =>
      AssignmentSettingsConfig.map((action: Action) => prepareAction(assignment, isStaff.value)(action))
        .filter(filterAction(assignment))
        .map(transformAction(assignment, isStaff.value))
        .sort(sortAction),
    )

    return {
      sender,
      actions,
      isStaff,
      isReturn,
      products,
      currency,
      assignment,
      isHidePrice,
      serviceLevel,
      documentsUrl,
      isLabelCreated,
      isInternational,
      outsideEu,
      invoiceCategory,
      hasCustomInvoice,
      totalInvoiceCosts,
      showDownloadButton,
      serviceLevelComment,
      distributionChannel,
      getDeliveryTypeLabel,
      getLinkToTrackingPage,
      toAddressString,
      formattedDate,
      formattedPrice,
      addressOwner,
      LABELS_RELEASED,
      REPORT_ACCEPTED,
      REPORT_RELEASED,
      OPEN,
    }
  },
})
</script>
